import moment from "moment";

import { RolesApi } from "@/Administration/Api/Roles/RolesApi";
import { AdminServiceGridSortColumn } from "@/Administration/Models/Enums/AdminServiceGridSortColumn";
import { RoleHeaderModel } from "@/Administration/Models/Role/RoleHeaderModel";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { Permission } from "@/Common/Api/Employee/Dto/Permission";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { RadioButtonv2 } from "@/Common/Components/Inputs/RadioButtonv2";
import { SpanControl } from "@/Common/Components/SpanControl";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { PrescriptionPrintType } from "@/Common/Models/Employee/Enums/Settings";
import { EnumMyleLoginPermission } from "@/Common/Models/EnumMyleLoginPermission";
import { IComboOrServiceItem } from "@/Common/Models/Services/BaseServiceDefinitionModel";
import { ServiceOrComboTableItem } from "@/Common/Models/Services/ServiceOrComboTableItem";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { FormMode, SortDirection } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import { RoleListFieldComponent } from "../Roles/Field/RolesListFieldComponent";
import { ProtectMedicalNoteComponent } from "./Allowlist/ProtectMedicalNoteComponent";
import { PermissionSettingsSectionComponent } from "./PermissionSettings/PermissionSettingsSectionComponent";
import { UserConfigurationSectionComponent } from "./PermissionSettings/UserConfigurationSectionComponent";
import { GroupSettingsComponent } from "./GroupSettingsComponent";

import styles from "./UserConfigurationInfoEditComponent.module.scss";

interface IUserConfigurationInfoComponentProps {
    employeeModel: EmployeeUIModel;
}

class UserConfigurationInfoComponentState {
    public employeeResult: Employee;
    public roleDefinitions: RoleHeaderModel[];
}

class UserConfigurationInfoComponent extends React.Component<IUserConfigurationInfoComponentProps, UserConfigurationInfoComponentState> {
    private c1Paddingtop: React.CSSProperties = { paddingTop: "5px" };
    private saveOptionKey: string = "EmployeeServiceComboGrid";

    constructor(props: IUserConfigurationInfoComponentProps) {
        super(props);

        const initialState = new UserConfigurationInfoComponentState();
        initialState.employeeResult = props.employeeModel.Model;
        this.state = initialState;
    }

    public componentDidMount(): void {
        if (cacheStore.ClinicSettings.EnableRoleBasedAccessControl)
            this.loadRolePermissionHeaders();

        $(window).trigger("resize");
    }

    public componentDidUpdate(prevProps: IUserConfigurationInfoComponentProps): void {
        const currentResult = this.props.employeeModel;
        const previousResult = prevProps.employeeModel;

        if (currentResult !== previousResult) {
            this.mutateState(s => { s.employeeResult = currentResult.Model; });
        }
    }

    public render(): JSX.Element {
        const employeeType: EmployeeType = this.props.employeeModel.Model.Type;
        const useRoleBasedPermissions: boolean = !this.props.employeeModel.Model.Permissions?.any();

        // settings UI improvement and role-based access control are not supported by API users
        const showUserSettingsUiImprovements = cacheStore.ClinicSettings.EnableUserSettingsUiImprovements && employeeType !== EmployeeType.Api;
        const showRoleBasedAccessControl = cacheStore.ClinicSettings.EnableRoleBasedAccessControl && employeeType !== EmployeeType.Api;

        return (
            <div>
                {employeeType !== EmployeeType.Api && (
                    <>
                        <div className="user-component">
                            <div className="flex-container heading" style={{ "borderBottom": "solid 1px #ccc", "margin": "20px 25px" }}>
                                <div className="h">
                                    <h3 className="thin-font">{cacheStore.GetString("AccountConfigurationLabel")}</h3>
                                </div>
                            </div>
                            <div className="psingle-column practice centered">
                                <div className="tb">
                                    {this.renderConfiguration()}
                                </div>
                            </div>
                        </div>
                        <div className="user-component">
                            <div className="flex-container heading" style={{ "borderBottom": "solid 1px #ccc", "margin": "20px 25px" }}>
                                <div className="h">
                                    <h3 className="thin-font">{cacheStore.GetString("PrintingOptionLabel")}</h3>
                                </div>
                            </div>
                            <div className="psingle-column practice centered">
                                <div className="tb">
                                    {this.renderPrintingOption()}
                                </div>
                            </div>
                        </div>
                    </>
                )}
                <div className="user-component">
                    <div className="flex-container heading" style={{ "borderBottom": "solid 1px #ccc", "margin": "20px 25px" }}>
                        <div className="h">
                            <h3 className="thin-font">{cacheStore.GetString("AccessSettingsLabel")}</h3>
                        </div>
                    </div>
                    <div className="psingle-column practice centered">
                        <div className="tb">
                            {this.renderAccessSettings(useRoleBasedPermissions, showUserSettingsUiImprovements, showRoleBasedAccessControl)}
                        </div>
                    </div>
                </div>
                {(showUserSettingsUiImprovements) && (
                    <UserConfigurationSectionComponent title={cacheStore.GetString("PermissionsLabel")}>
                        {this.renderPermissions(useRoleBasedPermissions)}
                    </UserConfigurationSectionComponent>
                )}
                {(employeeType !== EmployeeType.Assistant && employeeType !== EmployeeType.Api) && (
                    <div className="user-component">
                        <div className="flex-container heading" style={{ "margin": "20px 25px" }}>
                            <div className="h">
                                <h3 className="thin-font">{cacheStore.GetString("ServiceComboLabel")}</h3>
                            </div>
                            <div style={{ marginTop: "10px" }}>
                                <div className="flex-container search-settings" />
                            </div>

                        </div>
                        <div>
                            {this.renderServices()}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private renderConfiguration(): JSX.Element {
        let employee = this.props.employeeModel;
        return (
            <div className="tbc">
                <div>
                    <table>
                        <tbody>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("LanguageLabel")}</label></td>
                                <td className="c2"><strong>{(employee.Model.PreferredOfficialLanguageLookupId) ? (MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(employee.Model.PreferredOfficialLanguageLookupId))) : "--"}</strong></td>
                            </tr>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("TimeoutInactivityLabel")}</label></td>
                                <td className="c2"><strong>{(employee.Model.TimeoutDelay) ? employee.Model.TimeoutDelay : "--"}</strong></td>
                            </tr>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("ValidityPasswordLabel")}</label></td>
                                <td className="c2"><strong>{(employee.Model.ValidityPeriodPasswordDays) ? employee.Model.ValidityPeriodPasswordDays : "--"}</strong></td>
                            </tr>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("StartValidityUserLabel")}</label></td>
                                <td className="c2"><strong>{employee.Model.StartValidity == null ? "--" : moment(employee.Model.StartValidity).format("YYYY-MM-DD")}</strong></td>
                            </tr>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("EndValidityUserLabel")}</label></td>
                                <td className="c2"><strong>{employee.Model.EndValidity == null ? "--" : moment(employee.Model.EndValidity).format("YYYY-MM-DD")}</strong></td>
                            </tr>
                            {(employee.Model.Type === EmployeeType.Practician || employee.Model.Type === EmployeeType.Admin) && (
                                <>
                                    <tr>
                                        <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("PublicApi_EmployeeVisibleOnlineLabel")}</label></td>
                                        <td className="c2">
                                            <strong>
                                                <CheckboxControl
                                                    id="chkVisibleOnline"
                                                    disabled
                                                    checked={employee.Model.VisibleOnline}
                                                />
                                            </strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("BookingHubEnabledLabel")}</label></td>
                                        <td className="c2">
                                            <strong>
                                                <CheckboxControl
                                                    id="chkBookingHubEnabled"
                                                    disabled
                                                    checked={employee.Model.BookingHubEnabled}
                                                />
                                            </strong>
                                        </td>
                                    </tr>
                                    {employee.Model.BookingHubEnabled && (
                                        <>
                                            <tr>
                                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("HubFirstNameLabel")}</label></td>
                                                <td className="c2"><strong>{MyleHelper.ReplaceEmptyString(employee.Model.HubFirstName)}</strong></td>
                                            </tr>
                                            <tr>
                                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("HubLastNameLabel")}</label></td>
                                                <td className="c2"><strong>{MyleHelper.ReplaceEmptyString(employee.Model.HubLastName)}</strong></td>
                                            </tr>
                                        </>
                                    )}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    private renderPrintingOption(): JSX.Element {
        let employee = this.props.employeeModel;

        let selectedPrescriptionType = "regular_a";

        if (employee.Model.PrescriptionPrintType == PrescriptionPrintType.Thermal)
            selectedPrescriptionType = "thermal_a";
        else if (employee.Model.PrescriptionPrintType == PrescriptionPrintType.Mexico)
            selectedPrescriptionType = "mexico_a";

        return (
            <div className="tbc">
                <div>
                    <table>
                        <tbody>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("LabelsLabel")}</label></td>
                                <td className="c2">
                                    <div style={{ width: "600px" }}>
                                        <RadioButtonv2
                                            cssClass="settings-user-radioWidth"
                                            title={null}
                                            items={[
                                                new KeyValuePair("portrait", cacheStore.GetString("PortraitLabel")),
                                                new KeyValuePair("landscape", cacheStore.GetString("LandscapeLabel"))
                                            ].toImmutable()}
                                            htmlName="labels"
                                            selectedItem={employee.Model.PrintingSetting === true ? "landscape" : "portrait"}
                                            changeOperation={null}
                                            styleForEachradio={{ width: "22%", float: "left", marginTop: "5px" }}
                                            disabled
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("HeightSlashWidthLabel")}</label></td>
                                <td className="c2">
                                    <div style={{ width: "600px" }}>
                                        <RadioButtonv2
                                            cssClass="settings-user-radioWidth"
                                            title={null}
                                            items={[
                                                new KeyValuePair("small", cacheStore.GetString("ThreePerOneInchesLabel")),
                                                new KeyValuePair("large", cacheStore.GetString("FourPerOnePointFiveInchesLabel"))
                                            ].toImmutable()}
                                            htmlName="widthHeight"
                                            selectedItem={employee.Model.LargeLabelPrinting === false ? "small" : "large"}
                                            changeOperation={null}
                                            styleForEachradio={{ width: "22%", float: "left", marginTop: "5px" }}
                                            disabled
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("PrescriptionLabel")}</label></td>
                                <td className="c2">
                                    <div style={{ width: "600px" }}>
                                        <RadioButtonv2
                                            cssClass="settings-user-radioWidth"
                                            title={null}
                                            items={[
                                                new KeyValuePair("regular_a", cacheStore.GetString("RegularPrinterLabel")),
                                                new KeyValuePair("thermal_a", cacheStore.GetString("ThermicPrinterLabel")),
                                                new KeyValuePair("mexico_a", cacheStore.GetString("MexicoAndColombiaStandardsLbl"))
                                            ].toImmutable()}
                                            htmlName="prescription"
                                            selectedItem={selectedPrescriptionType}
                                            changeOperation={null}
                                            styleForEachradio={{ width: "22%", float: "left", marginTop: "5px" }}
                                            disabled
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("AppointmentConfirmationLabel")}</label></td>
                                <td className="c2">
                                    <div style={{ width: "600px" }}>
                                        <RadioButtonv2
                                            cssClass="settings-user-radioWidth"
                                            title={null}
                                            items={[
                                                new KeyValuePair("regular_b", cacheStore.GetString("RegularPrinterLabel")),
                                                new KeyValuePair("thermal_b", cacheStore.GetString("ThermicPrinterLabel"))
                                            ].toImmutable()}
                                            htmlName="appointment"
                                            selectedItem={employee.Model.IsRegularRDVConfirmPrinter === true ? "regular_b" : "thermal_b"}
                                            changeOperation={null}
                                            styleForEachradio={{ width: "22%", float: "left", marginTop: "5px" }}
                                            disabled
                                        />
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    private renderAccessSettings(useRoleBasedPermissions: boolean, showUserSettingsUiImprovements: boolean, showRoleBasedAccessControl: boolean): JSX.Element {
        const employee = this.props.employeeModel;
        const allPermissions: Permission[] = Permission.getPermissionsByEmployee(employee);

        const patientTabPermission = allPermissions.find(permission => permission.Name === "PatientTabPermission");
        const showMedicalNoteAllowlist = patientTabPermission && cacheStore.ClinicSettings.EnableMedicalNoteProtection && employee.Model.Permissions.some(employeePermission => employeePermission.toString() == patientTabPermission.Id);

        let allSites = cacheStore.GetSites(null);
        let siteList: SitesModel[] = [];

        siteList = employee.getSites();

        return (
            <div className="tbc" data-cy="userProfile-settingsView">
                {!showUserSettingsUiImprovements && employee.Type !== EmployeeType.Api && (
                    <GroupSettingsComponent
                        onDeleteGroupCallback={null}
                        onGroupSelectCallback={null}
                        selectedGroups={employee.Model.Groups}
                        editable={false}
                    />
                )}
                <div>
                    <table>
                        <tbody>
                            {showRoleBasedAccessControl && (
                                <tr>
                                    <td className="c1" style={{ "paddingTop": "5px", "verticalAlign": "top" }}><label className={styles.rolesLabel}>{cacheStore.GetString("RoleBasedAccessControl_RolesLabel")}</label></td>
                                    <td className="c2">
                                        <RoleListFieldComponent
                                            dataCy="role-list-field"
                                            selectedRolesList={this.props.employeeModel.Model.Roles}
                                            setSelectedRoles={null}
                                            formMode={FormMode.View}
                                            showWarning={!useRoleBasedPermissions}
                                        />
                                    </td>
                                </tr>
                            )}

                            <tr data-cy="userProfile-settingsViewSites">
                                <td className="c1" style={{ "paddingTop": "5px", "verticalAlign": "top" }}><label className="form-label">{cacheStore.GetString("SitesLabel")}</label></td>
                                <td className="c2" style={{ "paddingTop": "5px", "verticalAlign": "top" }}>
                                    {allSites.map((x: SiteUIModel, i: number) => (
                                        <div key={"site_" + i}>
                                            <CheckboxControl
                                                id={x.Id}
                                                operation={null}
                                                target={CheckboxTarget.Unassigned}
                                                label={x.Name}
                                                checked={(siteList && (siteList.some(z => z.Model.Id == x.Id))) ? true : false}
                                                dataCy={`userProfile-settingsViewSites-${i}`}
                                                disabled
                                            />
                                        </div>
                                    ))}
                                </td>
                            </tr>
                            {(!showUserSettingsUiImprovements) && (
                                <tr>
                                    <td className="c1" style={{ "paddingTop": "5px", "verticalAlign": "top" }}><label className="form-label">{cacheStore.GetString("PermissionsLabel")}</label></td>
                                    <td className="c2" style={{ "paddingTop": "5px", "verticalAlign": "top" }}>
                                        <div className={styles.permissionCheckboxesContainer}>
                                            {allPermissions.map((permission: Permission, i: number) => (
                                                <div id={permission.Name} key={"perm_" + i}>
                                                    <CheckboxControl
                                                        id={permission.Id}
                                                        operation={null}
                                                        target={CheckboxTarget.Unassigned}
                                                        label={EnumHelper.getVocab(EnumMyleLoginPermission, "Enums_EnumMyleLoginPermission_", permission.Name)}
                                                        checked={(employee.Model.Permissions && employee.Model.Permissions.some(employeePermission => employeePermission.toString() == permission.Id))}
                                                        dataCy={`permission-${permission.Name}`}
                                                        disabled
                                                    />
                                                </div>
                                            ))}
                                            {showMedicalNoteAllowlist && (
                                                <ProtectMedicalNoteComponent
                                                    onChange={null}
                                                    model={employee.MedicalNoteAllowlist}
                                                    dataCy="protectMedicalNoteComponent"
                                                    editable={false}
                                                />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    private renderPermissions(useRoleBasedPermissions: boolean): JSX.Element {
        const employee = this.props.employeeModel;

        return (
            <PermissionSettingsSectionComponent
                onAllowlistChange={null}
                onPermissionCheckChange={null}
                permissionIds={useRoleBasedPermissions ? this.getResolvedRolePermissions(employee.Model.Roles, this.state.roleDefinitions) : employee.Model.Permissions}
                allowlist={employee.MedicalNoteAllowlist}
                disabled={true}
            />
        );
    }

    private renderServices(): JSX.Element {
        const employee = this.props.employeeModel;
        const myleHelper = new MyleHelper();
        const columnsOptions = myleHelper.loadTableColumnOptionsFromSettings(this.saveOptionKey, this.getMyleTableColumnsDefaultOptions());

        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);
        const tableColumns = this.getTableColumns(columnsOptions);

        const serviceFilteredAdHoc: ServiceOrComboTableItem<IComboOrServiceItem>[] = employee.ServicesCombos
            ? employee.ServicesCombos.filter(x => x.IsAdHoc == false)
            : null;

        return (
            <div>
                <MyleTable
                    TableRenderOptions={tableRenderOptions}
                    TableId="employeeServicesCombos_table"
                    Columns={tableColumns}
                    DataSource={Immutable.fromJS(serviceFilteredAdHoc)}
                    RowClickOperation={null}
                    SaveOptionskeyTarget={this.saveOptionKey}
                    ColumnReorderTarget={MyleTableTarget.Unassigned}
                    ColumnSortTarget={MyleTableTarget.Unassigned}
                    NoScrollLimit={true}
                    CssClass="admin-table-override"
                />
            </div>
        );
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<ServiceOrComboTableItem<IComboOrServiceItem>>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "Site":
                        return {
                            renderCell: item => (
                                <div><SpanControl text={item.Site} /> </div>
                            )
                        };

                    case "Service":
                        return {
                            renderCell: item => (
                                <div><SpanControl text={item.Name} /> </div>
                            )
                        };

                    case "Type":
                        return {
                            renderCell: item => (
                                <div> <SpanControl text={item.TypeString} /> </div>
                            )
                        };

                    case "Speciality":
                        return {
                            renderCell: item => (
                                <div> <SpanControl text={item.Speciality} /> </div>
                            )
                        };

                    case "Duration":
                        return {
                            renderCell: item => (
                                <div> <SpanControl text={item.Duration + " min"} /> </div>
                            )
                        };
                }
            },
            false
        );
    }

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        var cols: MyleTableColumnOptions[] = [];

        var c_opt0 = new MyleTableColumnOptions();
        c_opt0.HeaderText = cacheStore.GetString("SiteDLabel");
        c_opt0.FieldName = "Site";
        c_opt0.IsFixedInPlace = true;
        c_opt0.IsSortable = true;
        c_opt0.RowClickEnabled = true;
        c_opt0.SortColumn = AdminServiceGridSortColumn.Site;
        c_opt0.SortDirection = SortDirection.ASC;
        c_opt0.WidthPercentageDefault = 25;

        var c_opt1 = new MyleTableColumnOptions();
        c_opt1.HeaderText = cacheStore.GetString("NameLabel");
        c_opt1.FieldName = "Service";
        c_opt1.RowClickEnabled = true;
        c_opt1.IsSortable = true;
        c_opt1.SortColumn = AdminServiceGridSortColumn.Name;
        c_opt1.SortDirection = SortDirection.ASC;
        c_opt1.WidthPercentageDefault = 20;

        var c_opt2 = new MyleTableColumnOptions();
        c_opt2.HeaderText = cacheStore.GetString("TypeLabel");
        c_opt2.FieldName = "Type";
        c_opt2.RowClickEnabled = true;
        c_opt2.IsSortable = true;
        c_opt2.SortColumn = AdminServiceGridSortColumn.Type;
        c_opt2.SortDirection = SortDirection.ASC;
        c_opt2.WidthPercentageDefault = 20;

        var c_opt3 = new MyleTableColumnOptions();
        c_opt3.HeaderText = cacheStore.GetString("SpecialityLabel");
        c_opt3.FieldName = "Speciality";
        c_opt3.RowClickEnabled = true;
        c_opt3.IsSortable = true;
        c_opt3.SortColumn = AdminServiceGridSortColumn.Speciality;
        c_opt3.SortDirection = SortDirection.ASC;
        c_opt3.WidthPercentageDefault = 25;

        var c_opt4 = new MyleTableColumnOptions();
        c_opt4.HeaderText = cacheStore.GetString("DurationLabel");
        c_opt4.FieldName = "Duration";
        c_opt4.RowClickEnabled = true;
        c_opt4.IsSortable = true;
        c_opt4.SortColumn = AdminServiceGridSortColumn.Duration;
        c_opt4.SortDirection = SortDirection.ASC;
        c_opt4.WidthPercentageDefault = 15;

        cols.push(c_opt0);
        cols.push(c_opt1);
        cols.push(c_opt2);
        cols.push(c_opt3);
        cols.push(c_opt4);

        return cols;
    }

    private loadTableRenderOptions(ColumnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        var defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: ColumnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: false,
            Autoresize: true,
            OnColumnWidthChange: null
        };

        return Immutable.fromJS(defaultTablePluginOptions);
    }

    private mutateState(mutation: (s: UserConfigurationInfoComponentState) => void, then: () => void = null): void {
        var s = new UserConfigurationInfoComponentState();
        mutation(s);
        this.setState(s, then);
    }

    private async loadRolePermissionHeaders(): Promise<void> {
        const roleDefinitions = await RolesApi.GetCachedRoleHeadersAsync();
        this.mutateState(s => s.roleDefinitions = roleDefinitions.RoleHeaders);
    }

    private getResolvedRolePermissions(selectedRoles: string[], roleDefinitions: RoleHeaderModel[]): string[] {
        if (!roleDefinitions || !selectedRoles)
            return [];

        const allPermissions = selectedRoles.map(roleId => {
            const role = roleDefinitions
                .firstOrDefault(definition => definition.Id === roleId);

            if (role == null) return [];
            return role.PermissionIds;
        });

        return [].concat(...allPermissions).distinct();
    }
}

export { UserConfigurationInfoComponent };
